import { FirebaseApp } from 'firebase/app'
import { fetchAndActivate, getRemoteConfig, getValue, RemoteConfig } from 'firebase/remote-config'
import { Observable, Subject } from 'rxjs'
import { ExperimentData } from 'src/app/interfaces/main'
import { customLocalStorage } from 'src/utils/utils'
import { LogService } from '../log.service'

export enum TEST_GROUP_KEY {
  // exp009 실험 key
  REFERRAL_HOME_FEED = 'x9',
  REFERRAL_PLATFORM = 'p',
  REFERRAL_USERID = 'ruid',
}

export enum TEST_GROUP_LOG_KEY {
  HOME_FEED = 'exp_009_group',
  REFERRAL_HOME_FEED = 'referral_exp_009_group',
  REFERRAL_PLATFORM = 'referral_platform',
  REFERRAL_USERID = 'referral_uid',
}

export enum TEST_GROUP_DEEPLINK_KEY {
  FEED_GROUP = 'feedGroup',
  REFERRAL_HOME_FEED = 'referralFeedGroup',
  REFERRAL_PLATFORM = 'referralPlatform',
  REFERRAL_USERID = 'referralUserId',
}

export const testGroupMap: { queryParamKey: TEST_GROUP_KEY; logKey: string; deeplinkKey: string }[] = [
  {
    queryParamKey: TEST_GROUP_KEY.REFERRAL_HOME_FEED,
    logKey: TEST_GROUP_LOG_KEY.REFERRAL_HOME_FEED,
    deeplinkKey: TEST_GROUP_DEEPLINK_KEY.REFERRAL_HOME_FEED,
  },
  {
    queryParamKey: TEST_GROUP_KEY.REFERRAL_PLATFORM,
    logKey: TEST_GROUP_LOG_KEY.REFERRAL_PLATFORM,
    deeplinkKey: TEST_GROUP_DEEPLINK_KEY.REFERRAL_PLATFORM,
  },
  {
    queryParamKey: TEST_GROUP_KEY.REFERRAL_USERID,
    logKey: TEST_GROUP_LOG_KEY.REFERRAL_USERID,
    deeplinkKey: TEST_GROUP_DEEPLINK_KEY.REFERRAL_USERID,
  },
]

export const getTestGroup = (key: TEST_GROUP_KEY): { queryParamKey: TEST_GROUP_KEY; logKey: string } | undefined => {
  return testGroupMap.find((item) => item.queryParamKey === key)
}

const experimentRandomValueKey = (expNo: number) => `EXP_${expNo}_RANDOM_VALUE`

export class ExperimentService {
  constructor(private logService: LogService) {}

  private remoteConfig: RemoteConfig | undefined
  private expValueChangedSubject = new Subject<
    { expNo: number; value: number | boolean | undefined; isRelatedToFeed?: boolean }[]
  >()
  exp009: number | undefined
  exp012 = false
  exp022 = 0
  exp024 = 0
  exp044 = 0 // 그룹케미 개선 실험
  exp072 = 0 // 영문 퀴즈 공유 타이틀 실험
  exp078 = 0 // 로그 튜토리얼 노출 실험
  exp085 = 0 /*광고 토글
  0: 바이럴X, 쿠팡1순위
  1: 바이럴중, 쿠팡2순위
  2: 바이럴피크, 쿠팡X
  */
  exp086 = 0 // 광고 순서 실험(0:테무-> 애드매니저, 1:테무->테무)
  exp087 = 0 //대기열 실험

  getExpValueChangedSubject(): Observable<
    { expNo: number; value: number | boolean | undefined; isRelatedToFeed?: boolean }[]
  > {
    return this.expValueChangedSubject.asObservable()
  }

  setExpLogProperties(experiments: ExperimentData[]): void {
    if (Array.isArray(experiments) && experiments.length > 0) {
      experiments.forEach(async (exp) => {
        this.setExperimentRandomValue(exp.key)
      })
    }

    this.exp009 = this.setNumberLogProperties(
      experiments.find((exp) => exp.key === 9),
      9
    )
    this.exp012 = this.setBooleanLogProperties(
      experiments.find((exp) => exp.key === 12),
      12
    )
    this.exp022 =
      this.setNumberLogProperties(
        experiments.find((exp) => exp.key === 22),
        22
      ) ?? 0
    this.exp024 =
      this.setNumberLogProperties(
        experiments.find((exp) => exp.key === 24),
        24
      ) ?? 0
    this.exp044 =
      this.setNumberLogProperties(
        experiments.find((exp) => exp.key === 44),
        44
      ) ?? 0
    this.exp072 =
      this.setNumberLogProperties(
        experiments.find((exp) => exp.key === 72),
        72
      ) ?? 0

    this.exp078 =
      this.setNumberLogProperties(
        experiments.find((exp) => exp.key === 78),
        78
      ) ?? 0
    this.exp085 =
      this.setNumberLogProperties(
        experiments.find((exp) => exp.key === 85),
        85
      ) ?? 0
    this.exp086 =
      this.setNumberLogProperties(
        experiments.find((exp) => exp.key === 86),
        86
      ) ?? 0
    this.exp087 =
      this.setNumberLogProperties(
        experiments.find((exp) => exp.key === 87),
        87
      ) ?? 0
    this.expValueChangedSubject.next([
      { expNo: 9, value: this.exp009, isRelatedToFeed: true },
      { expNo: 12, value: this.exp012 },
      { expNo: 22, value: this.exp022, isRelatedToFeed: true },
      { expNo: 24, value: this.exp024, isRelatedToFeed: true },
      { expNo: 44, value: this.exp044, isRelatedToFeed: true },
      { expNo: 72, value: this.exp072, isRelatedToFeed: true },
      { expNo: 78, value: this.exp078 },
      { expNo: 85, value: this.exp085 },
      { expNo: 86, value: this.exp086 },
      { expNo: 87, value: this.exp087 },
    ])
  }

  private setNumberLogProperties(data: ExperimentData | undefined, expNo: number): number | undefined {
    if (data === undefined) {
      return
    }
    const groupNumber = this.getExperimentGroupNumber(data)
    const properties = { [`exp${`${expNo}`.padStart(3, '0')}Group`]: `${groupNumber}` }
    this.logService.setUserConfig(properties)
    Object.keys(properties).forEach((key) => {
      this.logService.setSuperProperty(key, properties[key])
    })
    return groupNumber
  }

  private setBooleanLogProperties(data: ExperimentData | undefined, expNo: number): boolean {
    if (data === undefined) {
      return false
    }
    const groupNumber = this.getExperimentGroupNumber(data)
    const isExperimentalGroup = groupNumber === 1
    const properties = { [`exp${`${expNo}`.padStart(3, '0')}Group`]: `${isExperimentalGroup}` }
    this.logService.setUserConfig(properties)
    Object.keys(properties).forEach((key) => {
      this.logService.setSuperProperty(key, properties[key])
    })
    return isExperimentalGroup
  }

  /**
   * exp002, exp003 세팅
   */
  async setExperimentalVariables(): Promise<void> {
    try {
      this.remoteConfig && (await fetchAndActivate(this.remoteConfig))
      // response: {"value":"exp001_false","key":"is_ask_visible_on_home","source":0}
      const userGroup = await this.getRemoteConfigValue('user_group')
      this.logService.setUserConfig({ user_group: userGroup.value })
    } catch (error) {
      console.error(error)
    }
  }

  getFeedGroup(): number | undefined {
    return this.exp009
  }

  private async getRemoteConfigValue(key: string): Promise<{ key: string; value: string }> {
    if (!this.remoteConfig) return { key: '', value: '' }
    const result = getValue(this.remoteConfig, key)
    return {
      key,
      value: result.asString(),
    }
  }

  private async setExp(remoteConfigKey: string, callback: (result: boolean | string) => void): Promise<void> {
    const res = await this.getRemoteConfigValue(remoteConfigKey)
    const { value, key } = res
    if (!value.includes('exp')) {
      value === 'true' || value === 'false' ? callback(value === 'true') : callback(value)
    } else {
      const [expNumber, expValue] = value.split('_')
      if (expNumber !== undefined && expValue !== undefined) {
        expNumber === 'true' || value === 'false' ? callback(expNumber === 'true') : callback(expNumber)
        const userConfig: Record<string, string> = {}
        userConfig[`${expNumber}_${key}`] = expValue
        this.logService.setUserConfig(userConfig)
      }
    }
  }

  /**
   * 해당 실험의 난수 값 세팅
   * 1 ~ 1000
   */
  setExperimentRandomValue(expNo: number): void {
    const storedValue = customLocalStorage.getItem(experimentRandomValueKey(expNo))
    if (storedValue) {
      return
    }
    const randomValue = Math.floor(Math.random() * 1000) + 1
    customLocalStorage.setItem(experimentRandomValueKey(expNo), randomValue + '')
  }

  /** 해당 실험의 난수 값 가져오기 */
  getExperimentRandomValue(expNo: number): number | undefined {
    const value = customLocalStorage.getItem(experimentRandomValueKey(expNo))
    return value ? +value : undefined
  }

  getExperimentGroupNumber(data: ExperimentData): number | undefined {
    const { key, groups } = data
    const value = this.getExperimentRandomValue(key)
    const totalWeight = groups.map((group) => group.weight).reduce((acc, cur) => acc + cur)
    if (value) {
      let sumWeight = 0
      for (const group of groups) {
        sumWeight += group.weight
        if (sumWeight / totalWeight >= value / 1000) {
          return group.number
        }
      }
    }
    return
  }

  initRemoteConfig(firebaseApp: FirebaseApp): void {
    this.remoteConfig = getRemoteConfig(firebaseApp)
  }

  appendTestGroupParams(url: string, type: 'quiz' | 'test' = 'quiz', userId?: number): string {
    let result: string = url
    if (url.includes('?')) {
      result += `&x9=${this.getFeedGroup()}`
    } else {
      result += `?x9=${this.getFeedGroup()}`
    }
    result += `&p=w`
    if (type === 'test') {
      result += `&${TEST_GROUP_KEY.REFERRAL_USERID}=${userId && userId > 0 ? userId : this.makeRandomId(6)}`
    }
    return result
  }

  /** 숫자로만 구성될 것 방지하기 위해 맨 앞자리는 무조건 알파벳 */
  private makeRandomId(length: number): string {
    let result = ''
    const alphabets = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
    const numbers = '0123456789'
    result += alphabets.charAt(Math.floor(Math.random() * alphabets.length))
    const characters = `${alphabets}${numbers}`
    const charactersLength = characters.length
    for (let i = 0; i < length - 1; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  }
}
